import { getRealtimeDatabaseCurrentTimestamp } from '@framework/firebase/rtdb';
import { KeysRepository, RefBuilder, ServerValue } from '@framework/repository';
import { AI_ASSISTANT_MEMBER_SETTINGS_MAX_PER_REQUEST, RTDBPath } from 'balus-common';
import type { GroupId, GroupLicenseUpdateLogJSON, ModelingFormat, OperatorJSON, UserId } from 'balus-common';
import { GroupEntity } from './GroupEntity';
import { createGroupEntityRepository } from './GroupEntityRepository';
import type { GroupMemberRole } from './GroupMemberRole';
import { WorkspaceOperation } from '@workspace/domain/WorkspaceOperation';
import { GroupMemberInvitationBuilder } from '../GroupMemberInvitation';
import { GroupMemberInvitationOperation } from '../GroupMemberInvitation/GroupMemberInvitationOperation';
import { UserEntity } from '@user/UserEntity';
import { GroupSetting } from './GroupSetting';
import { GroupLicense } from '../GroupLicense';
import { trpcClient } from '@framework/trpcClient';
import { toUserWebappHref } from '@config/localOrigins';

export class GroupOperation {
  /**
   * グループを新しく作成します。このグループに対応するグループライセンスも同時に作成します。
   * 作成に失敗した時には例外を投げます。
   *
   * OperatorUser が利用可能なメソッドです。
   *
   * @param name グループ名
   * @param email 招待するメールアドレス
   * @returns 作成したグループIDと招待ID
   * @throws Error グループ作成に失敗した場合
   */
  static async create(
    name: string,
    email: string,
    options: { withoutInvitation?: boolean; defaultModelingFormat?: ModelingFormat } = {}
  ): Promise<{ groupId: GroupId; invitationIds: string[] }> {
    const now = await getRealtimeDatabaseCurrentTimestamp();
    const groupEntity = GroupEntity.buildNew(
      name,
      now,
      options.defaultModelingFormat ? GroupSetting.buildNew(options.defaultModelingFormat) : undefined
    );

    const message = '';
    const baseUrl = toUserWebappHref(
      new URL('/', window.location.href).toString().replace('admin.', '').replace('admin-dot-', '')
    );

    const groupLicense = new GroupLicense('trial', 20, now.addMonths(2).endOf('day'));
    const invitationsUpdate = (() => {
      // withoutInvitation オプションが true の場合には、招待メールを送信しない
      if (options.withoutInvitation) {
        return {};
      }

      const builder = new GroupMemberInvitationBuilder(
        groupEntity,
        UserEntity.AdminConsoleUser,
        [email],
        'admin',
        null,
        message,
        baseUrl
      );
      return builder.buildInvitationUpdates(now);
    })();

    const groupLicenseUpdateLog: GroupLicenseUpdateLogJSON = {
      license: groupLicense.dump(),
      internalNote: null,
      updatedBy: email,
      reason: 'Balus管理者によるグループ作成時の初期ライセンス設定',
      createdAt: now.toUnixTimestamp(),
    };

    await RefBuilder.ref().update({
      [RTDBPath.Group.groupEntityPath(groupEntity.id)]: groupEntity.dump(),
      [RTDBPath.Group.groupLicensePath(groupEntity.id)]: groupLicense.dump(),
      [RTDBPath.Group.groupLicenseUpdateLogPath(groupEntity.id, now.toUnixTimestamp().toString())]:
        groupLicenseUpdateLog,
      ...invitationsUpdate,
    });

    const invitationIds = options.withoutInvitation
      ? []
      : Object.values(invitationsUpdate).map((invitation) => invitation.id);

    return { groupId: groupEntity.id, invitationIds };
  }

  /**
   * 指定のグループの名前と新規登録時に利用可能なログイン方法を更新します。
   * 更新に失敗した時には例外を投げます。
   *
   * 対象グループの管理者ロールを保持したユーザが利用可能なメソッドです。
   *
   * @param groupId 更新対象のグループID
   * @param name 変更後のグループ名
   * @param setting 変更後のグループ設定
   * @param imageUrl グループアイコン
   * @throws Error 更新に失敗した場合
   */
  static async update(groupId: GroupId, name: string, setting: GroupSetting, imageUrl: string | null): Promise<void> {
    await RefBuilder.ref(RTDBPath.Group.groupEntityPath(groupId)).update({
      name,
      setting: setting.dump(),
      imageUrl,
      updatedAt: ServerValue.TIMESTAMP,
    });
  }

  /**
   * 指定のグループの指定ユーザのロールを変更します。
   * 更新に失敗した時には例外を投げます。
   *
   * 対象グループの管理者ロールを保持したユーザが利用可能なメソッドです。
   * 自分自身のロールを変更することはできません。
   *
   * @param groupId 更新対象のグループID
   * @param userId 更新対象のユーザID
   * @param role 変更後のグループメンバーロール
   * @throws Error 更新に失敗した場合
   */
  static async updateMemberRole(groupId: GroupId, userId: UserId, role: GroupMemberRole): Promise<void> {
    await RefBuilder.ref(RTDBPath.Group.memberRolePath(groupId, userId)).set(role);
  }

  /**
   * グループから指定した複数のメンバーを削除します。
   * 所属しているワークスペースがある場合には、それらのワークスペースからもメンバーが削除されます。
   * 削除に失敗した時には例外を投げます。
   *
   * 対象グループの管理者ロールを保持したユーザが利用可能なメソッドです。
   *
   * @throws Error 削除に失敗した場合
   */
  static async removeMembers(groupId: GroupId, userIds: UserId[]): Promise<void> {
    const values: Record<string, null> = {};

    // 指定されたユーザを所属するワークスペースから削除する
    await Promise.all(
      userIds.map(async (userId) => {
        const workspaceIds = await new KeysRepository(
          RTDBPath.Workspace.assignedGroupWorkspaceIndexPath(userId, groupId)
        ).get();

        await Promise.all(
          workspaceIds.map(async (workspaceId) => {
            Object.assign(values, await WorkspaceOperation.buildRemovableMemberValues(workspaceId, groupId, [userId]));
          })
        );
      })
    );

    // AIアシスタントのメンバー別許可フラグを削除する。
    // member-roles を消す前に呼ぶことで、tRPC側の「対象がグループメンバーであること」検証を通す。
    // 入力上限に合わせて分割送信する。
    for (let i = 0; i < userIds.length; i += AI_ASSISTANT_MEMBER_SETTINGS_MAX_PER_REQUEST) {
      const chunk = userIds.slice(i, i + AI_ASSISTANT_MEMBER_SETTINGS_MAX_PER_REQUEST);
      await trpcClient.group.updateAIAssistantMemberEnabled.mutate({
        groupId,
        settings: chunk.map((userId) => ({ userId, enabled: false })),
      });
    }

    // グループからメンバーを削除する
    userIds.forEach((userId) => {
      Object.assign(values, {
        [RTDBPath.Group.memberRolePath(groupId, userId)]: null,
        [RTDBPath.Group.assignedGroupIndexValuePath(userId, groupId)]: null,
      });
    });

    // 上記で作ったパスを一括削除
    await RefBuilder.ref().update(values);

    // 対象ユーザのビューモデル個別権限設定を全て削除する
    await trpcClient.viewModel.individualMember.removeAllIndividualPermissions.mutate({ groupId, userIds });
  }

  /**
   * グループの論理削除を行います。
   * 削除に失敗した時には例外を投げます。
   *
   * * グループ配下の全てのワークスペースを論理削除します
   * * グループに所属する全てのメンバーをグループから削除します
   * * 削除日、削除ユーザIDを記録します
   * @throws Error 削除に失敗した場合
   */
  static async logicalDelete(groupId: GroupId, trashedBy: OperatorJSON['email']): Promise<void> {
    const group = await createGroupEntityRepository(groupId).get();
    if (!group) {
      throw new Error(`グループが見つかりません: ${groupId}`);
    }
    // 既に論理削除済みならば、何も処理せずに論理削除失敗を返す
    if (group.trashedAt) {
      throw new Error(`グループは既に論理削除されています: ${groupId}`);
    }

    // グループへのメンバー招待状を無効化する
    await GroupMemberInvitationOperation.allDeactivate(groupId);

    // 全てのワークスペースを論理削除する
    const workspaceIds = await new KeysRepository(RTDBPath.Group.workspaceIndexPath(groupId)).get();
    await Promise.all(workspaceIds.map((workspaceId) => WorkspaceOperation.logicalDelete(workspaceId, trashedBy)));

    // ビューモデル個別権限設定を無効化する
    await RefBuilder.ref(RTDBPath.Group.enableIndividualViewModelPermissionsPath(groupId)).set(false);

    // 全てのユーザをグループから除外
    const userIds = await new KeysRepository(RTDBPath.Group.memberRolesPath(groupId)).get();
    await this.removeMembers(group.id, userIds);

    // 更新日、削除日、削除ユーザIDを設定
    await new Promise<void>((resolve, reject) => {
      RefBuilder.ref(RTDBPath.Group.groupEntityPath(group.id)).update(
        {
          updatedAt: ServerValue.TIMESTAMP,
          trashedAt: ServerValue.TIMESTAMP,
          trashedBy: trashedBy,
        },
        (err) => {
          if (err) {
            console.error(err);
            reject(new Error(`グループエンティティの更新に失敗しました: ${err.message || err}`));
          } else {
            resolve();
          }
        }
      );
    });
  }
}
